import React, { useRef, useCallback, useEffect } from "react";
import { API_BASE_URL } from "../config/api";
import { Avatar } from "../ui/components/Avatar";
import { Badge } from "../ui/components/Badge";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { Table } from "../ui/components/Table";
import { Drawer } from "../ui/components/Drawer";
import {
  FeatherAlertCircle,
  FeatherAlertTriangle,
  FeatherClock,
  FeatherDownload,
  FeatherEye,
  FeatherFileText,
  FeatherX,
} from "@subframe/core";
import {
  getMetrics,
  getDistribution,
  getOverdueCustomers,
  getLatePayers,
  ARAgingMetrics,
  ARAgingBucket,
  ARAgingDistributionResponse,
  OverdueCustomer,
  LatePayer,
} from "../services/arAging";
import { formatPlAmount } from "../features/client_dashboard/presentation/utils/formatPlAmount";

function formatCurrencyForCSV(value: number | null | undefined): string {
  if (value === null || value === undefined) return "0";
  // Format as plain number with 2 decimal places, using Indian number format
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatRawAmountWithCommas(
  amount: number,
  currencyCode: string,
  currencySymbol: string,
): string {
  const locale =
    currencyCode.toUpperCase() === "USD" || currencyCode.toUpperCase() === "AED"
      ? "en-US"
      : "en-IN";
  const s = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${currencySymbol}${s}`;
}

function formatDateDdMmYyyy(value: string | null | undefined): string {
  if (!value) return "-";

  // Fast path for ISO-like dates: 2026-04-28 (optionally with time after)
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd}/${mm}/${yyyy}`;
  }

  // Fallback for other date strings
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
}

type CustomerInvoice = {
  invoiceNumber: string;
  date: string;
  dueDate: string;
  amount: number;
  balance: number;
  status: string;
  daysOverdue: number;
};

export default function ARAgingPage() {
  // State management
  const asOfToday = React.useMemo(
    () =>
      new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [],
  );
  const [metrics, setMetrics] = React.useState<ARAgingMetrics[]>([]);
  const [distribution, setDistribution] = React.useState<ARAgingBucket[]>([]);
  const [distributionSummary, setDistributionSummary] = React.useState<
    Record<
      string,
      Pick<
        ARAgingDistributionResponse,
        | "asOfDate"
        | "totalOutstandingAmount"
        | "overdueAmount"
        | "notOverdueAmount"
        | "currencySymbol"
      >
    >
  >({});
  const [overdueCustomers, setOverdueCustomers] = React.useState<
    OverdueCustomer[]
  >([]);
  const [latePayers, setLatePayers] = React.useState<LatePayer[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  // Pagination state for overdue customers
  const [overdueCustomersOffset, setOverdueCustomersOffset] =
    React.useState<number>(0);
  const [overdueCustomersHasMore, setOverdueCustomersHasMore] =
    React.useState<boolean>(false);
  const [overdueCustomersTotal, setOverdueCustomersTotal] =
    React.useState<number>(0);
  const OVERDUE_CUSTOMERS_LIMIT = 10;

  // Drawer state
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [selectedCustomerId, setSelectedCustomerId] =
    React.useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] =
    React.useState<string>("");
  const [drawerContext, setDrawerContext] = React.useState<
    "overdue" | "late_payers"
  >("overdue");
  const [drawerLateInvoiceCount, setDrawerLateInvoiceCount] =
    React.useState<number>(0);
  const [invoiceCurrencySymbol, setInvoiceCurrencySymbol] =
    React.useState<string>("₹");
  const [customerInvoices, setCustomerInvoices] = React.useState<
    CustomerInvoice[]
  >([]);
  const [invoicesLoading, setInvoicesLoading] = React.useState<boolean>(false);
  const [invoicesLoadingMore, setInvoicesLoadingMore] =
    React.useState<boolean>(false);
  const [invoicesHasMore, setInvoicesHasMore] = React.useState<boolean>(false);
  const [invoicesOffset, setInvoicesOffset] = React.useState<number>(0);
  const [invoicesTotal, setInvoicesTotal] = React.useState<number>(0);

  const INVOICES_PAGE_SIZE = 20;
  const invoicesScrollRef = useRef<HTMLDivElement>(null);
  const invoicesSentinelRef = useRef<HTMLDivElement>(null);

  const fetchCustomerInvoices = useCallback(
    async (
      params: { customerId?: string; customerName?: string },
      offset: number = 0,
    ) => {
      const isInitial = offset === 0;
      if (isInitial) {
        setInvoicesLoading(true);
        setCustomerInvoices([]);
      } else {
        setInvoicesLoadingMore(true);
      }
      try {
        const token = localStorage.getItem("token");
        const headers: HeadersInit = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const queryKey = params.customerId ? "customer_id" : "customer";
        const queryValue = params.customerId
          ? params.customerId
          : params.customerName || "";
        const url = `${API_BASE_URL}/api/ar-aging/customer-invoices?${queryKey}=${encodeURIComponent(
          queryValue,
        )}&limit=${INVOICES_PAGE_SIZE}&offset=${offset}`;
        const response = await fetch(url, { headers });

        if (!response.ok) {
          throw new Error("Failed to fetch customer invoices");
        }

        const json = await response.json();
        const { data, pagination } = json;

        if (isInitial) {
          setCustomerInvoices(data ?? []);
        } else {
          setCustomerInvoices((prev) => [...prev, ...(data ?? [])]);
        }
        setInvoicesTotal(pagination?.total ?? 0);
        setInvoicesHasMore(pagination?.has_more ?? false);
        setInvoicesOffset(offset + (data?.length ?? 0));
      } catch (err) {
        console.error("Failed to load customer invoices:", err);
        if (isInitial) {
          setCustomerInvoices([]);
        }
      } finally {
        if (isInitial) {
          setInvoicesLoading(false);
        } else {
          setInvoicesLoadingMore(false);
        }
      }
    },
    [],
  );

  const fetchMoreInvoices = useCallback(() => {
    if (
      !invoicesHasMore ||
      invoicesLoadingMore ||
      (!selectedCustomerId && !selectedCustomerName)
    )
      return;
    fetchCustomerInvoices(
      { customerId: selectedCustomerId, customerName: selectedCustomerName },
      invoicesOffset,
    );
  }, [
    invoicesHasMore,
    invoicesLoadingMore,
    selectedCustomerId,
    selectedCustomerName,
    invoicesOffset,
    fetchCustomerInvoices,
  ]);

  useEffect(() => {
    if (
      !drawerOpen ||
      !invoicesScrollRef.current ||
      !invoicesSentinelRef.current
    )
      return;
    if (invoicesLoading || !invoicesHasMore || invoicesLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchMoreInvoices();
      },
      { root: invoicesScrollRef.current, rootMargin: "100px", threshold: 0.1 },
    );
    observer.observe(invoicesSentinelRef.current);
    return () => observer.disconnect();
  }, [
    drawerOpen,
    invoicesLoading,
    invoicesHasMore,
    invoicesLoadingMore,
    fetchMoreInvoices,
  ]);

  const handleViewInvoices = (
    customer: {
      customerId?: string;
      customerName: string;
      context?: "overdue" | "late_payers";
      lateInvoiceCount?: number;
    },
    currencySymbol?: string,
  ) => {
    setSelectedCustomerId(customer.customerId || "");
    setSelectedCustomerName(customer.customerName);
    setDrawerContext(customer.context ?? "overdue");
    setDrawerLateInvoiceCount(customer.lateInvoiceCount ?? 0);
    setInvoiceCurrencySymbol(currencySymbol?.trim() || "₹");
    setDrawerOpen(true);
    setInvoicesHasMore(false);
    setInvoicesOffset(0);
    setInvoicesTotal(0);
    fetchCustomerInvoices(
      { customerId: customer.customerId, customerName: customer.customerName },
      0,
    );
  };

  const handleDownloadCSV = () => {
    try {
      const rows: string[][] = [];

      // Header
      rows.push(["AR Aging Report", new Date().toLocaleDateString()]);
      rows.push([]);

      // Metrics by Currency
      metrics.forEach((currencyMetrics) => {
        rows.push([
          `Metrics - ${currencyMetrics.currencyCode} (${currencyMetrics.currencySymbol})`,
        ]);
        rows.push([
          "Total Receivables",
          formatCurrencyForCSV(currencyMetrics.totalReceivables),
          currencyMetrics.currencyCode,
        ]);
        rows.push([
          "Overdue Amount",
          formatCurrencyForCSV(currencyMetrics.overdueAmount),
          currencyMetrics.currencyCode,
        ]);
        rows.push([
          "Oldest Outstanding",
          `${currencyMetrics.oldestOutstanding} days`,
          "",
        ]);
        // rows.push([
        //   "Avg Collection Period",
        //   `${currencyMetrics.avgCollectionPeriod} days`,
        //   "",
        // ]);
        rows.push([]);
      });

      // Group distribution by currency
      const distByCurrency: Record<string, ARAgingBucket[]> = {};
      distribution.forEach((bucket) => {
        if (!distByCurrency[bucket.currencyCode]) {
          distByCurrency[bucket.currencyCode] = [];
        }
        distByCurrency[bucket.currencyCode].push(bucket);
      });

      // Distribution by Currency
      Object.entries(distByCurrency).forEach(([currencyCode, buckets]) => {
        const currencySymbol = buckets[0]?.currencySymbol || "";
        rows.push([`Aging Distribution - ${currencyCode} (${currencySymbol})`]);
        rows.push(["Range", "Amount", "Currency", "Percentage"]);
        buckets.forEach((bucket) => {
          rows.push([
            `${bucket.range} days`,
            formatCurrencyForCSV(bucket.amount),
            bucket.currencyCode,
            `${bucket.percentage}%`,
          ]);
        });
        rows.push([]);
      });

      // Overdue Customers
      rows.push(["Overdue Customers"]);
      rows.push([
        "Customer Name",
        "Outstanding Amount",
        "Currency",
        "Invoice Count",
        "Oldest Age (days)",
        "Status",
      ]);
      overdueCustomers.forEach((customer) => {
        rows.push([
          customer.customer,
          formatCurrencyForCSV(customer.amount),
          customer.currencyCode,
          customer.invoiceCount.toString(),
          customer.oldestAge.toString(),
          customer.status,
        ]);
      });
      rows.push([]);

      // Late Payers
      if (latePayers.length > 0) {
        rows.push(["Frequently Late Payers"]);
        rows.push([
          "Customer",
          "Late Frequency (%)",
          "Late Count",
          "Total Count",
          "Avg Delay (days)",
          "Longest Delay (days)",
          "Outstanding",
          "Currency",
          "Trend",
        ]);
        latePayers.forEach((payer) => {
          rows.push([
            payer.customer,
            `${payer.lateFrequency}%`,
            payer.lateCount.toString(),
            payer.totalCount.toString(),
            payer.avgDelay.toString(),
            payer.longestDelay.toString(),
            formatCurrencyForCSV(payer.outstanding),
            payer.currencyCode,
            payer.trend,
          ]);
        });
      }

      // Convert to CSV
      const csvContent = rows
        .map((row) =>
          row
            .map((cell) => `"${cell.toString().replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      // Download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `ar_aging_report_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download CSV:", err);
      alert("Failed to download CSV. Please try again.");
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Loading AR Aging data...");

      const [metricsData, distData, overdueData, latePayersData] =
        await Promise.all([
          getMetrics({}),
          getDistribution({}),
          getOverdueCustomers({
            limit: OVERDUE_CUSTOMERS_LIMIT,
            offset: overdueCustomersOffset,
          }),
          getLatePayers({}),
        ]);
      console.log("AR Aging data loaded successfully:", {
        metricsData,
        distData,
        overdueData,
        latePayersData,
      });

      setMetrics(metricsData);
      setDistribution(distData.buckets ?? []);
      setDistributionSummary((prev) => ({
        ...prev,
        [distData.currencyCode ?? "INR"]: {
          asOfDate: distData.asOfDate,
          totalOutstandingAmount: distData.totalOutstandingAmount,
          overdueAmount: distData.overdueAmount,
          notOverdueAmount: distData.notOverdueAmount,
          currencySymbol: distData.currencySymbol,
        },
      }));
      setOverdueCustomers(overdueData.data);
      setOverdueCustomersHasMore(overdueData.pagination.has_more);
      setOverdueCustomersTotal(overdueData.pagination.total);
      setLatePayers(latePayersData);
    } catch (err: any) {
      console.error("Failed to load AR Aging data:", err);
      const errorMessage =
        err?.message || "Failed to load AR Aging data. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [overdueCustomersOffset]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload overdue customers when pagination changes
  React.useEffect(() => {
    const loadOverdueCustomers = async () => {
      try {
        const overdueData = await getOverdueCustomers({
          limit: OVERDUE_CUSTOMERS_LIMIT,
          offset: overdueCustomersOffset,
        });
        setOverdueCustomers(overdueData.data);
        setOverdueCustomersHasMore(overdueData.pagination.has_more);
        setOverdueCustomersTotal(overdueData.pagination.total);
      } catch (err: any) {
        console.error("Failed to load overdue customers:", err);
      }
    };

    if (!loading) {
      loadOverdueCustomers();
    }
  }, [overdueCustomersOffset, loading]);

  const getAgingBucketBgClassByRange = (range: string): string | undefined => {
    const normalized = (range ?? "").trim().toLowerCase();
    if (!normalized) return undefined;

    // Not overdue bucket (synthetic or API-provided)
    if (normalized.includes("not overdue")) return "bg-green-500";

    // 91+ (or similar)
    if (normalized.includes("+")) {
      const n = Number((normalized.match(/\d+/) ?? [])[0]);
      if (!Number.isNaN(n) && n >= 91) return "bg-red-600";
    }

    // Parse numeric ranges like "0-30", "31 - 60 days", "61 to 90"
    const nums = (normalized.match(/\d+/g) ?? []).map((n) => Number(n));
    if (nums.length >= 2) {
      const [from, to] = nums;
      if (from <= 0 && to <= 30) return "bg-neutral-500"; // 0–30 grey
      if (from >= 31 && to <= 60) return "bg-blue-500"; // 31–60 blue
      if (from >= 61 && to <= 90) return "bg-yellow-500"; // 61–90 yellow
      if (from >= 91) return "bg-red-600"; // 91+ red (when expressed as range)
    }

    // Fallback: if it contains a single number, map by threshold
    if (nums.length === 1) {
      const [n] = nums;
      if (n <= 30) return "bg-neutral-500";
      if (n <= 60) return "bg-blue-500";
      if (n <= 90) return "bg-yellow-500";
      return "bg-red-600";
    }

    return undefined;
  };

  // Group distribution by currency
  const distributionByCurrency = React.useMemo(() => {
    const grouped: Record<string, ARAgingBucket[]> = {};
    distribution.forEach((bucket) => {
      if (!grouped[bucket.currencyCode]) {
        grouped[bucket.currencyCode] = [];
      }
      grouped[bucket.currencyCode].push(bucket);
    });
    return grouped;
  }, [distribution]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-50">
        <span className="text-body font-body text-subtext-color">
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-4">
          <span className="text-body font-body text-error-600">{error}</span>
          <Button onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-neutral-50 px-6 py-6 overflow-auto">
        <div className="w-full">
          <span className="text-heading-3 font-heading-3 text-subtext-color">
            As of {asOfToday}
          </span>
        </div>
        {/* Header Card */}
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Receivables
              </span>
              <span className="text-body font-body text-subtext-color">
                Track money owed by customers and identify overdue collections.
              </span>
            </div>
            <div className="flex items-center gap-2 no-print">
              <Button
                variant="brand-primary"
                size="medium"
                className="shadow-sm"
                icon={<FeatherDownload className="h-4 w-4" />}
                onClick={handleDownloadCSV}
              >
                Download CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Aging Distribution - Grouped by Currency */}
        {Object.entries(distributionByCurrency).map(
          ([currencyCode, buckets]) => {
            const currencySymbol = buckets[0]?.currencySymbol || "";
            const summary = distributionSummary[currencyCode];
            const displayBuckets = summary
              ? ([
                  {
                    range: "Not overdue",
                    amount: summary.notOverdueAmount ?? 0,
                    percentage:
                      summary.totalOutstandingAmount > 0
                        ? Math.round(
                            (summary.notOverdueAmount /
                              summary.totalOutstandingAmount) *
                              1000,
                          ) / 10
                        : 0,
                    color: "success",
                    currencyCode,
                    currencySymbol: summary.currencySymbol ?? currencySymbol,
                  },
                  ...buckets,
                ] as ARAgingBucket[])
              : buckets;
            const adjustedDisplayBuckets = displayBuckets.map((bucket) => ({
              ...bucket,
              color:
                getAgingBucketBgClassByRange(bucket.range) ??
                bucket.color ??
                "bg-neutral-500",
            }));
            const totalAmount =
              summary?.totalOutstandingAmount ??
              adjustedDisplayBuckets.reduce((sum, bucket) => sum + bucket.amount, 0);

            return (
              <div
                key={currencyCode}
                className="flex w-full flex-col items-start gap-4 rounded-xl border border-solid border-neutral-200 bg-white px-8 py-8 shadow-sm"
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Receivables Ageing Distribution - {currencyCode} (
                      {currencySymbol})
                    </span>
                    <span className="text-body font-body text-subtext-color">
                      Breakdown of overdue invoices by age
                    </span>
                    {summary ? (
                      <div className="mt-2 flex w-full flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-neutral-50 px-4 py-3">
                        <div className="flex items-baseline gap-2">
                          <span className="h-2.5 w-2.5 flex-none rounded-full bg-neutral-400" />
                          <span className="text-caption font-caption text-subtext-color">
                            Outstanding
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {formatPlAmount(
                              summary.totalOutstandingAmount,
                              summary.currencySymbol,
                            )}
                          </span>
                        </div>
                        <div className="hidden h-4 w-px bg-neutral-200 mobile:hidden" />
                        <div className="flex items-baseline gap-2">
                          <span className="h-2.5 w-2.5 flex-none rounded-full bg-success-500" />
                          <span className="text-caption font-caption text-subtext-color">
                            Not overdue
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {formatPlAmount(
                              summary.notOverdueAmount,
                              summary.currencySymbol,
                            )}
                          </span>
                        </div>
                        <div className="hidden h-4 w-px bg-neutral-200 mobile:hidden" />
                        <div className="flex items-baseline gap-2">
                          <span className="h-2.5 w-2.5 flex-none rounded-full bg-error-600" />
                          <span className="text-caption font-caption text-subtext-color">
                            Overdue
                          </span>
                          <span className="text-body-bold font-body-bold text-error-700">
                            {formatPlAmount(
                              summary.overdueAmount,
                              summary.currencySymbol,
                            )}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex w-full flex-col items-start gap-3">
                  <div className="flex h-12 w-full flex-none items-center overflow-hidden rounded-lg">
                    {adjustedDisplayBuckets.map((bucket, index) => {
                      const widthPercent =
                        totalAmount > 0
                          ? (bucket.amount / totalAmount) * 100
                          : 0;
                      const showLabel = widthPercent > 8 && bucket.amount > 0;
                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-center self-stretch border-r border-white/70 last:border-r-0 ${
                            bucket.color || "bg-neutral-500"
                          }`}
                          style={{
                            width: `${widthPercent}%`,
                            minWidth: widthPercent > 0 ? "40px" : "0",
                            display: widthPercent > 0 ? "flex" : "none",
                          }}
                        >
                          {showLabel && bucket.amount && (
                            <span className="text-caption-bold font-caption-bold text-white whitespace-nowrap">
                              {formatPlAmount(
                                bucket.amount,
                                bucket.currencySymbol,
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex w-full items-center justify-between flex-wrap gap-2">
                    {adjustedDisplayBuckets.map((bucket, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className={`flex h-3 w-3 flex-none items-start rounded-sm ${
                            bucket.color || "bg-neutral-500"
                          }`}
                        />
                        <span className="text-caption font-caption text-default-font">
                          {bucket.range === "Not overdue"
                            ? "Not overdue:"
                            : `${bucket.range} days:`}{" "}
                          {formatPlAmount(bucket.amount, bucket.currencySymbol)}{" "}
                          ({bucket.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {summary && summary.overdueAmount > 0 && (
                  <div className="flex w-full items-center gap-2 rounded-lg border border-solid border-warning-200 bg-warning-50 px-4 py-3">
                    <FeatherAlertTriangle className="text-body font-body text-warning-600" />
                    <span className="text-body font-body text-warning-700">
                      {formatPlAmount(
                        summary.overdueAmount,
                        summary.currencySymbol,
                      )}{" "}
                      of receivables are overdue and may impact near-term cash
                      flow.
                    </span>
                  </div>
                )}
              </div>
            );
          },
        )}

        {/* Metrics Cards - Grouped by Currency */}
        {metrics.map((currencyMetrics, currencyIndex) => (
          <div
            key={currencyMetrics.currencyCode}
            className="flex w-full flex-col gap-4"
          >
            <div className="flex w-full items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                {currencyMetrics.currencyCode} ({currencyMetrics.currencySymbol}
                )
              </span>
            </div>
            <div className="flex w-full gap-4 items-stretch mobile:flex-col mobile:flex-nowrap mobile:gap-4">
              <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Total Receivables
                  </span>
                  <FeatherFileText className="text-body font-body text-brand-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {formatRawAmountWithCommas(
                    currencyMetrics.totalReceivables,
                    currencyMetrics.currencyCode,
                    currencyMetrics.currencySymbol,
                  )}
                </span>
                {currencyMetrics.convertedTotalReceivables != null &&
                currencyMetrics.convertedCurrencyCode &&
                currencyMetrics.convertedCurrencySymbol ? (
                  <span className="text-caption font-caption text-subtext-color">
                    {formatRawAmountWithCommas(
                      currencyMetrics.convertedTotalReceivables,
                      currencyMetrics.convertedCurrencyCode,
                      currencyMetrics.convertedCurrencySymbol,
                    )}{" "}
                    ({currencyMetrics.convertedCurrencyCode})
                  </span>
                ) : null}
                <span className="text-caption font-caption text-subtext-color">
                  Outstanding customer invoices
                </span>
              </div>

              <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Overdue Amount
                  </span>
                  <FeatherAlertCircle className="text-body font-body text-warning-600" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {formatRawAmountWithCommas(
                      currencyMetrics.overdueAmount,
                      currencyMetrics.currencyCode,
                      currencyMetrics.currencySymbol,
                    )}
                  </span>
                  {currencyMetrics.overdueAmount > 0 && (
                    <Badge variant="warning">Overdue</Badge>
                  )}
                </div>
                {currencyMetrics.convertedOverdueAmount != null &&
                currencyMetrics.convertedCurrencyCode &&
                currencyMetrics.convertedCurrencySymbol ? (
                  <span className="text-caption font-caption text-subtext-color">
                    {formatRawAmountWithCommas(
                      currencyMetrics.convertedOverdueAmount,
                      currencyMetrics.convertedCurrencyCode,
                      currencyMetrics.convertedCurrencySymbol,
                    )}{" "}
                    ({currencyMetrics.convertedCurrencyCode})
                  </span>
                ) : null}
                <span className="text-caption font-caption text-subtext-color">
                  Invoices overdue beyond due date
                </span>
              </div>

              <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Oldest Outstanding
                  </span>
                  <FeatherClock className="text-body font-body text-error-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {currencyMetrics.oldestOutstanding} days
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Longest unpaid invoice
                </span>
              </div>

              {/* <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Avg Collection Period
                  </span>
                  <FeatherCalendar className="text-body font-body text-success-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {currencyMetrics.avgCollectionPeriod} days
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Avg. time customers take to pay
                </span>
              </div> */}
            </div>
          </div>
        ))}

        {/* Overdue Receivables Table */}
        <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Overdue Receivables
              </span>
              <span className="text-body font-body text-subtext-color">
                Customers with invoices overdue beyond 30 days
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col items-start overflow-hidden overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Customer Name</Table.HeaderCell>
                  <Table.HeaderCell>Overdue Amount</Table.HeaderCell>
                  <Table.HeaderCell>Invoice Count</Table.HeaderCell>
                  <Table.HeaderCell>Oldest Age</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Action</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {overdueCustomers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <span className="text-body font-body text-subtext-color">
                      No overdue customers
                    </span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                overdueCustomers.map((customer, index) => {
                  const initials = customer.customer
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();
                  const avatarVariant =
                    customer.status === "critical" ? "error" : "warning";
                  const ageColor =
                    customer.oldestAge > 90
                      ? "text-error-600"
                      : customer.oldestAge > 60
                        ? "text-warning-600"
                        : "text-default-font";

                  return (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            variant={avatarVariant}
                            size="small"
                            image=""
                            square={true}
                          >
                            {initials}
                          </Avatar>
                          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                            {customer.customer}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {formatPlAmount(
                            customer.amount,
                            customer.currencySymbol,
                          )}{" "}
                          ({customer.currencyCode})
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {customer.invoiceCount}{" "}
                          {customer.invoiceCount === 1 ? "invoice" : "invoices"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={`whitespace-nowrap text-body font-body ${ageColor}`}
                        >
                          {customer.oldestAge} days
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          variant={
                            customer.status === "critical" ? "error" : "warning"
                          }
                        >
                          {customer.status === "critical"
                            ? "Critical"
                            : "Overdue"}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEye />}
                          onClick={() =>
                            handleViewInvoices(
                              {
                                customerId: customer.customerId,
                                customerName: customer.customer,
                              },
                              customer.currencySymbol,
                            )
                          }
                        >
                          View invoices
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table>
          </div>
          {/* Pagination Controls */}
          {overdueCustomersTotal > 0 && (
            <div className="flex w-full items-center justify-between gap-4 pt-4">
              <span className="text-body font-body text-subtext-color">
                Showing {overdueCustomersOffset + 1} to{" "}
                {Math.min(
                  overdueCustomersOffset + OVERDUE_CUSTOMERS_LIMIT,
                  overdueCustomersTotal,
                )}{" "}
                of {overdueCustomersTotal} customers
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={overdueCustomersOffset === 0}
                  onClick={() =>
                    setOverdueCustomersOffset(
                      Math.max(
                        0,
                        overdueCustomersOffset - OVERDUE_CUSTOMERS_LIMIT,
                      ),
                    )
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={!overdueCustomersHasMore}
                  onClick={() =>
                    setOverdueCustomersOffset(
                      overdueCustomersOffset + OVERDUE_CUSTOMERS_LIMIT,
                    )
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Frequently Late Payers Table */}
        {latePayers.length > 0 && (
          <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Frequently Late Payers
                </span>
                <span className="text-body font-body text-subtext-color">
                  Customers who often pay after due date
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-start overflow-hidden overflow-x-auto">
              <Table
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Customer</Table.HeaderCell>
                    <Table.HeaderCell>Late frequency</Table.HeaderCell>
                    <Table.HeaderCell>Avg delay</Table.HeaderCell>
                    <Table.HeaderCell>Longest delay</Table.HeaderCell>
                    <Table.HeaderCell>Outstanding</Table.HeaderCell>
                    <Table.HeaderCell>Trend</Table.HeaderCell>
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {latePayers.slice(0, 10).map((payer, index) => {
                  // const trendIcon =
                  //   payer.trend === "worsening"
                  //     ? FeatherTrendingUp
                  //     : payer.trend === "improving"
                  //     ? FeatherTrendingDown
                  //     : FeatherMinus;
                  const trendVariant =
                    payer.trend === "worsening"
                      ? "error"
                      : payer.trend === "improving"
                        ? "success"
                        : "neutral";
                  const trendLabel =
                    payer.trend === "worsening"
                      ? "Getting worse"
                      : payer.trend === "improving"
                        ? "Improving"
                        : "Stable";

                  return (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="flex flex-col items-start gap-1">
                          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                            {payer.customer}
                          </span>
                          <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                            Invoices: {payer.totalCount}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col items-start gap-2">
                          <span className="whitespace-nowrap text-body font-body text-default-font">
                            {payer.lateCount} / {payer.totalCount} invoices late
                          </span>
                          <div className="flex h-1.5 w-32 flex-none items-start overflow-hidden rounded-full bg-neutral-100">
                            <div
                              className={`flex items-start self-stretch ${
                                payer.lateFrequency > 60
                                  ? "bg-error-500"
                                  : "bg-warning-500"
                              }`}
                              style={{ width: `${payer.lateFrequency}%` }}
                            />
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {payer.avgDelay} days
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={`whitespace-nowrap text-body font-body ${
                            payer.longestDelay > 30
                              ? "text-error-600"
                              : "text-warning-600"
                          }`}
                        >
                          {payer.longestDelay} days
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={`whitespace-nowrap text-body-bold font-body-bold ${
                            payer.outstanding > 100000
                              ? "text-error-600"
                              : "text-warning-600"
                          }`}
                        >
                          {formatPlAmount(
                            payer.outstanding,
                            payer.currencySymbol,
                          )}{" "}
                          ({payer.currencyCode})
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          variant={trendVariant}
                          // icon={React.createElement(trendIcon)}
                        >
                          {trendLabel}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="neutral-tertiary"
                            size="small"
                            icon={<FeatherEye />}
                            onClick={() =>
                              handleViewInvoices(
                                {
                                  customerId: payer.customerId,
                                  customerName: payer.customer,
                                  context: "late_payers",
                                  lateInvoiceCount: payer.lateCount,
                                },
                                payer.currencySymbol,
                              )
                            }
                          >
                            View invoices
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
          <span className="text-caption font-caption text-subtext-color text-center">
            Figures are based on current books and may change as entries are
            finalized
          </span>
        </div>
      </div>

      {/* Customer Invoices Drawer */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Drawer.Content className="w-[600px]">
          <div className="flex w-full flex-col items-start gap-6 p-6">
            {/* Header */}
            <div className="flex w-full items-center justify-between shrink-0">
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Invoices for {selectedCustomerName}
                </span>
                <span className="text-body font-body text-subtext-color">
                  {invoicesLoading
                    ? "Loading..."
                    : invoicesTotal > 0
                      ? drawerContext === "late_payers"
                        ? `${drawerLateInvoiceCount} ${drawerLateInvoiceCount === 1 ? "late invoice" : "late invoices"}`
                        : `${customerInvoices.length} of ${invoicesTotal} ${invoicesTotal === 1 ? "invoice" : "invoices"}`
                      : "No invoices found"}
                </span>
              </div>
              <IconButton
                variant="neutral-tertiary"
                size="medium"
                icon={<FeatherX />}
                onClick={() => setDrawerOpen(false)}
              />
            </div>

            {/* Content */}
            {invoicesLoading ? (
              <div className="flex w-full items-center justify-center py-12">
                <span className="text-body font-body text-subtext-color">
                  Loading invoices...
                </span>
              </div>
            ) : customerInvoices.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center py-12 gap-2">
                <FeatherFileText className="text-[32px] text-subtext-color" />
                <span className="text-body font-body text-subtext-color">
                  No invoices found for this customer
                </span>
              </div>
            ) : (
              <div
                ref={invoicesScrollRef}
                className="flex w-full flex-col items-start gap-4 max-h-[calc(100vh-200px)] overflow-y-auto"
              >
                {customerInvoices.map((invoice, index) => (
                  <div
                    key={`${invoice.invoiceNumber}-${index}`}
                    className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-neutral-border bg-default-background p-4"
                  >
                    <div className="flex w-full items-start justify-between">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Invoice #{invoice.invoiceNumber}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Date: {invoice.date}
                        </span>
                      </div>
                      <Badge
                        variant={
                          invoice.status === "paid"
                            ? "success"
                            : invoice.daysOverdue > 0
                              ? "error"
                              : "warning"
                        }
                      >
                        {invoice.status === "paid"
                          ? "Paid"
                          : invoice.daysOverdue > 0
                            ? `Overdue ${invoice.daysOverdue}d`
                            : "Pending"}
                      </Badge>
                    </div>

                    <div className="flex w-full items-center justify-between">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-caption font-caption text-subtext-color">
                          Amount
                        </span>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {formatPlAmount(
                            invoice.amount,
                            invoiceCurrencySymbol,
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-caption font-caption text-subtext-color">
                          Balance
                        </span>
                        <span
                          className={`text-body-bold font-body-bold ${
                            invoice.balance > 0
                              ? "text-error-600"
                              : "text-success-600"
                          }`}
                        >
                          {formatPlAmount(
                            invoice.balance,
                            invoiceCurrencySymbol,
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-caption font-caption text-subtext-color">
                          Due Date
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatDateDdMmYyyy(invoice.dueDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                <div
                  ref={invoicesSentinelRef}
                  className="h-4 w-full shrink-0"
                />
                {invoicesLoadingMore && (
                  <div className="flex w-full items-center justify-center py-4">
                    <span className="text-caption font-caption text-subtext-color">
                      Loading more...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Drawer.Content>
      </Drawer>
    </>
  );
}
